import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, filter, switchMap, take, throwError } from 'rxjs';
import { AuthService } from './auth';

const AUTH_URLS = ['/User/authenticate', '/User/refresh-token', '/User/revoke-token'];

function isAuthUrl(url: string): boolean {
  return AUTH_URLS.some((authUrl) => url.includes(authUrl));
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const token = authService.token();

  const authReq = token
    ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
    : req;

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status !== 401 || isAuthUrl(req.url)) {
        return throwError(() => error);
      }

      if (!authService.isRefreshing) {
        authService.startRefresh();

        return authService.refreshToken().pipe(
          switchMap(() => {
            authService.finishRefresh(true);
            const retryReq = req.clone({
              setHeaders: { Authorization: `Bearer ${authService.token()}` },
            });
            return next(retryReq);
          }),
          catchError((refreshError) => {
            authService.finishRefresh(false);
            authService.logout();
            return throwError(() => refreshError);
          })
        );
      }

      return authService.refresh$.pipe(
        filter((ready) => ready),
        take(1),
        switchMap(() => {
          const retryReq = req.clone({
            setHeaders: { Authorization: `Bearer ${authService.token()}` },
          });
          return next(retryReq);
        })
      );
    })
  );
};
